import React, { useState, useEffect, useRef, useCallback } from "react";
import { MapContainer, TileLayer, Marker, Popup, Tooltip } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import {
  Modal,
  Button,
  Dropdown,
  Form,
  Loader,
  Icon,
  Label,
} from "semantic-ui-react";
import { APICall } from "../api";
import { addCoordinatesToOperators } from "../utils/capToCoordinates";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import ModaleModifica from "./ModaleModifica";

// Fix per le icone di Leaflet in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Vista iniziale, centro Italia
const ITALY_CENTER = [41.8719, 12.5674];
const ITALY_ZOOM = 6;

export default function MappaOperatori({ open, onClose }) {
  const [operatori, setOperatori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedOperatore, setSelectedOperatore] = useState(null);
  const [showDettaglio, setShowDettaglio] = useState(false);

  const nomeInputRef = useRef(null);
  const cognomeInputRef = useRef(null);
  const telefonoInputRef = useRef(null);
  const [lastFocusedField, setLastFocusedField] = useState(null); // tracciamo quale campo del form (nome, cogn, tel) ha il focus per non perderlo al re-render della mappa

  const [parametri, setParametri] = useState({
    codice: "",
    nazionalita: "",
    zona: "",
    sesso: "",
    citta: "",
    asl: "",
    provincia: "",
    municipi: "",
    cap: "",
    coordinate: "",
    patente: "",
    turni_accessi: "",
    offerta_assistenza: "",
    tipo_collaborazione: "",
    nome_query: "",
    cognome_query: "",
    telefono_query: "",
  });

  const [opzioni, setOpzioni] = useState({
    codice: [],
    cap: [],
    citta: [],
    zona: [],
    provincia: [],
    municipi_distretti_roma: [],
    asl: [],
    nazionalita: [],
    coordinate: [],
    patente: [],
    turni_accessi: [],
    offerta_assistenza: [],
    tipo_collaborazione: [],
  });

  const opzioniSesso = [
    { key: "n", value: "", text: "Tutti" },
    { key: "m", value: "m", text: "Maschio" },
    { key: "f", value: "f", text: "Femmina" },
  ];

  useEffect(() => {
    if (!open) return;

    const configurazioni = [
      { endpoint: "/codici", chiave: "codice" },
      { endpoint: "/cap", chiave: "cap" },
      { endpoint: "/citta", chiave: "citta" },
      { endpoint: "/zona", chiave: "zona" },
      { endpoint: "/provincia", chiave: "provincia" },
      { endpoint: "/municipi", chiave: "municipi_distretti_roma" },
      { endpoint: "/asl", chiave: "asl" },
      { endpoint: "/nazionalita", chiave: "nazionalita" },
      { endpoint: "/coordinate", chiave: "coordinate" },
      { endpoint: "/patente", chiave: "patente" },
      { endpoint: "/turniaccessi", chiave: "turni_accessi" },
      { endpoint: "/offertaassistenza", chiave: "offerta_assistenza" },
      { endpoint: "/tipocollaborazione", chiave: "tipo_collaborazione" },
    ];

    const fetchOpzioni = async () => {
      setLoading(true);

      for (const config of configurazioni) {
        try {
          const response = await APICall.get(config.endpoint);
          const data = response.data;

          const nuoveOpzioni = data.map((item) => ({
            key: item[config.chiave],
            value: item[config.chiave],
            text: item[config.chiave],
          }));

          // Ordina alfabeticamente
          const opzioniOrdinate = [...nuoveOpzioni].sort((a, b) => {
            if (!a.text && !b.text) return 0;
            if (!a.text) return 1;
            if (!b.text) return -1;
            return a.text.localeCompare(b.text, "it", { sensitivity: "base" });
          });

          // Aggiorna lo stato opzioni
          setOpzioni((prev) => ({
            ...prev,
            [config.chiave]: [
              { key: "", value: "", text: "Tutti" },
              ...opzioniOrdinate,
            ],
          }));
        } catch (error) {
          console.error(`Errore nel caricamento di ${config.endpoint}`, error);
        }
      }
      setLoading(false);
    };
    fetchOpzioni();
  }, [open]);

  // Carica gli operatori
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        fetchOperatori();
      }, 1000);

      return () => clearTimeout(timer); // il return è una cleanup function: viene chiamata prima di rieseguire la useEffect e quando viene smontato il componente (unmount). Cancella il timer se i parametri cambiano prima di 500ms
    }
  }, [open, parametri]);

  const fetchOperatori = async () => {
    setLoading(true);
    setError(null);

    try {
      const queryString = Object.entries(parametri)
        .filter(([key, value]) => value !== "" && value.length !== 0)
        .map(([key, value]) => `${key}=${value}`)
        .join("&");

      const url = queryString ? `/mappa?${queryString}` : "/mappa";
      const response = await APICall.get(url);

      if (response.data.success) {
        const operatoriConCoordinate = addCoordinatesToOperators(
          response.data.data,
        );
        setOperatori(operatoriConCoordinate);
      }
    } catch (err) {
      console.error("Errore caricamento operatori:", err);
      setError("Errore nel caricamento degli operatori");
    } finally {
      setLoading(false);
    }
  };

  // mantieni il focus sui campi di ricerca testuale al re-render del componente
  useEffect(() => {
    if (!loading && lastFocusedField) {
      // mettere un timeout a 0 ci permette di accodare il cambiamento di focus al rendering del componente, ed evitare che avvenga durante l'aggiornamento del DOM
      setTimeout(() => {
        if (lastFocusedField === "nome_query" && nomeInputRef.current) {
          nomeInputRef.current.focus();
        } else if (
          lastFocusedField === "cognome_query" &&
          cognomeInputRef.current
        ) {
          cognomeInputRef.current.focus();
        } else if (
          lastFocusedField === "telefono_query" &&
          telefonoInputRef.current
        ) {
          telefonoInputRef.current.focus();
        }
      }, 0);
    }
  }, [loading, lastFocusedField]);

  /**
   * Gestisce il cambio di valore nei dropdown e nei campi input
   * @param {Event} e - Evento del componente
   * @param {Object} data - Oggetto con name e value del campo
   */
  const handleChange = (e, { name, value }) => {
    setParametri((prevState) => {
      const newParams = { ...prevState, [name]: value };
      return newParams;
    });
  };

  /**
   * Resetta tutti i filtri
   */
  const handleReset = () => {
    setParametri({
      codice: "",
      nazionalita: "",
      zona: "",
      sesso: "",
      citta: "",
      asl: "",
      provincia: "",
      municipi: "",
      cap: "",
      coordinate: "",
      patente: "",
      turni_accessi: "",
      offerta_assistenza: "",
      tipo_collaborazione: "",
      nome_query: "",
      cognome_query: "",
      telefono_query: "",
    });
    // dopo il reset ricarica tutti gli operatori senza filtri
  };

  // Apri modale dettagliato
  const handleMarkerClick = (operatore) => {
    setSelectedOperatore(operatore);
    setShowDettaglio(true);
  };

  // icona personalizzata per i cluster
  const createClusterCustomIcon = (cluster) => {
    return L.divIcon({
      html: `<div class="cluster-icon">${cluster.getChildCount()}</div>`,
      className: "custom-cluster-icon",
      iconSize: L.point(40, 40, true),
    });
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="fullscreen"
      className="mappa-modal"
    >
      <Modal.Header>
        Mappa Operatori
        <Button icon="close" floated="right" onClick={onClose} basic circular />
      </Modal.Header>

      <Modal.Content className="mappa-content-wrapper">
        <div className="mappa-layout">
          {/** SIDEBAR DI SINISTRA CON I FILTRI */}
          <div className="mappa-filtri-sidebar">
            <h3 className="filtri-title">Filtri di Ricerca</h3>

            {/** FORM CON I FILTRI */}
            <Form className="filtri-form" size="small">
              {/** CAMPI DI RICERCA TESTUALE */}
              <Form.Input
                label="Nome"
                name="nome_query"
                placeholder="Cerca per nome..."
                value={parametri.nome_query}
                inputRef={nomeInputRef}
                onFocus={() => setLastFocusedField("nome_query")}
                onChange={(e) =>
                  handleChange(e, {
                    name: e.target.name,
                    value: e.target.value,
                  })
                }
                icon="user"
              />

              <Form.Input
                label="Cognome"
                name="cognome_query"
                placeholder="Cerca per cognome..."
                value={parametri.cognome_query}
                inputRef={cognomeInputRef}
                onFocus={() => setLastFocusedField("cognome_query")}
                onChange={(e) =>
                  handleChange(e, {
                    name: e.target.name,
                    value: e.target.value,
                  })
                }
                icon="user outline"
              />

              <Form.Input
                label="Telefono (ultime 4 cifre)"
                name="telefono_query"
                placeholder="Es: 1234"
                value={parametri.telefono_query}
                inputRef={telefonoInputRef}
                onFocus={() => setLastFocusedField("telefono_query")}
                onChange={(e) =>
                  handleChange(e, {
                    name: e.target.name,
                    value: e.target.value,
                  })
                }
                maxLength="4"
                pattern="[0-9]*"
                icon="phone"
              />
              <div
                style={{ margin: "1.5rem 0", borderBottom: "1px solid #ddd" }}
              ></div>
              {/** DROPDOWN FILTRI */}

              <Form.Field>
                <label>Codice Prof.</label>
                <Dropdown
                  placeholder="Seleziona codice..."
                  fluid
                  selection
                  multiple
                  search
                  clearable
                  options={opzioni.codice}
                  name="codice"
                  value={parametri.codice}
                  onChange={handleChange}
                />
              </Form.Field>

              <Form.Field>
                <label>Nazionalità</label>
                <Dropdown
                  placeholder="Seleziona nazionalità"
                  fluid
                  selection
                  multiple
                  search
                  clearable
                  options={opzioni.nazionalita}
                  name="nazionalita"
                  value={parametri.nazionalita}
                  onChange={handleChange}
                />
              </Form.Field>

              <Form.Field>
                <label>Zona</label>
                <Dropdown
                  placeholder="Seleziona zona..."
                  fluid
                  selection
                  multiple
                  search
                  clearable
                  options={opzioni.zona}
                  name="zona"
                  value={parametri.zona}
                  onChange={handleChange}
                />
              </Form.Field>

              <Form.Field>
                <label>Sesso</label>
                <Dropdown
                  placeholder="Seleziona sesso..."
                  fluid
                  selection
                  options={opzioniSesso}
                  name="sesso"
                  value={parametri.sesso}
                  onChange={handleChange}
                />
              </Form.Field>

              <Form.Field>
                <label>Città</label>
                <Dropdown
                  placeholder="Seleziona città..."
                  fluid
                  selection
                  multiple
                  search
                  clearable
                  options={opzioni.citta}
                  name="citta"
                  value={parametri.citta}
                  onChange={handleChange}
                />
              </Form.Field>

              <Form.Field>
                <label>ASL</label>
                <Dropdown
                  placeholder="Seleziona ASL..."
                  fluid
                  selection
                  multiple
                  search
                  clearable
                  options={opzioni.asl}
                  name="asl"
                  value={parametri.asl}
                  onChange={handleChange}
                />
              </Form.Field>

              <Form.Field>
                <label>Provincia</label>
                <Dropdown
                  placeholder="Seleziona provincia..."
                  fluid
                  selection
                  multiple
                  search
                  clearable
                  options={opzioni.provincia}
                  name="provincia"
                  value={parametri.provincia}
                  onChange={handleChange}
                />
              </Form.Field>

              <Form.Field>
                <label>Municipi / Distretti Roma</label>
                <Dropdown
                  placeholder="Seleziona municipio..."
                  fluid
                  selection
                  multiple
                  search
                  clearable
                  options={opzioni.municipi_distretti_roma}
                  name="municipi"
                  value={parametri.municipi}
                  onChange={handleChange}
                />
              </Form.Field>

              <Form.Field>
                <label>CAP</label>
                <Dropdown
                  placeholder="Seleziona CAP..."
                  fluid
                  selection
                  multiple
                  search
                  clearable
                  options={opzioni.cap}
                  name="cap"
                  value={parametri.cap}
                  onChange={handleChange}
                />
              </Form.Field>

              <Form.Field>
                <label>Coordinate</label>
                <Dropdown
                  placeholder="Seleziona coordinate..."
                  fluid
                  selection
                  multiple
                  search
                  clearable
                  options={opzioni.coordinate}
                  name="coordinate"
                  value={parametri.coordinate}
                  onChange={handleChange}
                />
              </Form.Field>

              <Form.Field>
                <label>Patente</label>
                <Dropdown
                  placeholder="Seleziona patente..."
                  fluid
                  selection
                  multiple
                  options={opzioni.patente}
                  name="patente"
                  value={parametri.patente}
                  onChange={handleChange}
                />
              </Form.Field>
              <Form.Field>
                <label>Disponibilità (Turni/Accessi)</label>
                <Dropdown
                  placeholder="Seleziona disponibilità..."
                  fluid
                  selection
                  multiple
                  search
                  options={opzioni.turni_accessi}
                  name="turni_accessi"
                  value={parametri.turni_accessi}
                  onChange={handleChange}
                />
              </Form.Field>
              <Form.Field>
                <label>Offerta Assistenza</label>
                <Dropdown
                  placeholder="Seleziona offerta..."
                  fluid
                  selection
                  multiple
                  search
                  clearable
                  options={opzioni.offerta_assistenza}
                  name="offerta_assistenza"
                  value={parametri.offerta_assistenza}
                  onChange={handleChange}
                />
              </Form.Field>
              <Form.Field>
                <label>Tipo Collaborazione</label>
                <Dropdown
                  placeholder="Seleziona collab."
                  fluid
                  selection
                  multiple
                  search
                  options={opzioni.tipo_collaborazione}
                  name="tipo_collaborazione"
                  value={parametri.tipo_collaborazione}
                  onChange={handleChange}
                />
              </Form.Field>
            </Form>

            {/** PULSANTE RESET */}
            <Button
              className="reset-button"
              onClick={handleReset}
              fluid
              style={{ marginTop: "1rem" }}
            >
              <Icon name="undo" />
              Reset Filtri
            </Button>
            {/** Contatore Operatori */}
            <div className="contatore-operatori">
              <Label size="large" color="blue">
                <Icon name="map marker alternate" />
                {operatori.length} operatori sulla mappa
              </Label>
            </div>
          </div>

          {/** AREA DI DESTRA CON LA MAPPA */}
          <div className="mappa-container" style={{ position: "relative" }}>
            {loading && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  zIndex: 1000,
                }}
              >
                <Loader active inline="centered">
                  Caricamento...
                </Loader>
              </div>
            )}
            {error && (
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 1000,
                  padding: "20px",
                  backgroundColor: "#fff3cd",
                  border: "1px solid #ffc107",
                  borderRadius: "5px",
                }}
              >
                <div className="error-message">{error}</div>
              </div>
            )}
            <MapContainer
              center={ITALY_CENTER}
              zoom={ITALY_ZOOM}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              <MarkerClusterGroup
                chunkedLoading
                iconCreateFunction={createClusterCustomIcon}
              >
                {operatori.map((operatore) => (
                  <Marker
                    key={operatore.id}
                    position={[operatore.gps.lat, operatore.gps.lng]}
                    eventHandlers={{
                      click: () => handleMarkerClick(operatore),
                    }}
                  >
                    <Tooltip
                      permanent={false}
                      direction="top"
                      offset={[0, -10]}
                    >
                      <div className="marker-tooltip">
                        <strong>
                          {operatore.cognome} {operatore.nome}
                        </strong>
                        <br />
                        <span className="codice-badge">{operatore.codice}</span>
                        <br />
                        {operatore.citta} ({operatore.provincia})
                        <br />
                        CAP:{operatore.cap}
                      </div>
                    </Tooltip>
                  </Marker>
                ))}
              </MarkerClusterGroup>
            </MapContainer>
          </div>
        </div>
      </Modal.Content>

      <Modal.Actions onClick={onClose}>Chiudi</Modal.Actions>

      {/* Modale Dettaglio Operatore */}
      {selectedOperatore && (
        <ModaleModifica
          open={showDettaglio}
          onClose={() => setShowDettaglio(false)}
          user={selectedOperatore}
          readOnly={true}
          onUpdate={() => {}} // non viene usato in modalità readOnly ma la prop è richiesta
        />
      )}
    </Modal>
  );
}
